import { useMemo, useState } from "react";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import Footer from "../Footer";
import Header from "../Header/Header";
import { useLanguage } from "../contexts/LanguageContext";
import { img } from "../utils/cloudinaryImage";
import { hiddenSupTour } from "../data/tours/hiddenSup";
import { oneDayAestheticTour } from "../data/tours/oneDayAesthetic";
import { surfingTour } from "../data/tours/surfing";
import s from "./DroneMapPage.module.scss";

const labels = {
  JP: {
    navTitle: "宮古島ドローンマップ",
    heroKicker: "Miyako Drone Map",
    heroTitle: "宮古島ドローンマップ",
    heroLead:
      "空から眺める宮古島の絶景を、地図から探せるページです。気になるスポットを選んで、ドローン映像でその魅力を体験できます。",
    openMap: "マップを開いて絶景を探す",
    mapFilter: "エリアから探す",
    close: "閉じる",
    tourCta: "このスポットのツアーを見る",
    toursTitle: "このスポットにおすすめのツアー",
    popularTitle: "人気の絶景スポット",
    howTitle: "使い方はとっても簡単",
    allTours: "すべてのツアーを見る",
    relatedTours: "関連ツアー",
    spotList: "すべてのスポット",
    details: "詳細を見る",
    howSteps: [
      ["マップから探す", "気になる絶景スポットを地図から選べます。"],
      ["映像を楽しむ", "ドローン映像で、現地の美しい景色を確認できます。"],
      ["ツアーを見る", "その場所に近い体験や撮影ツアーへ進めます。"],
      ["特別な思い出に", "気に入った景色を旅の目的地にできます。"],
    ],
  },
  EN: {
    navTitle: "Miyako Drone Map",
    heroKicker: "Miyako Drone Map",
    heroTitle: "Miyako Drone Map",
    heroLead:
      "Explore Miyakojima's scenic ocean spots from the map, then preview each location through drone footage.",
    openMap: "Open the map and explore",
    mapFilter: "Browse by area",
    close: "Close",
    tourCta: "View tours for this spot",
    toursTitle: "Recommended tours for this spot",
    popularTitle: "Popular scenic spots",
    howTitle: "How it works",
    allTours: "View all tours",
    relatedTours: "Related tours",
    spotList: "All spots",
    details: "Details",
    howSteps: [
      ["Find from the map", "Choose scenic spots directly from the map."],
      ["Watch the view", "Preview the atmosphere with drone footage."],
      ["View tours", "Continue to tours that visit nearby spots."],
      ["Make it memorable", "Turn the view you love into your trip plan."],
    ],
  },
  中文: {
    navTitle: "宫古岛无人机地图",
    heroKicker: "Miyako Drone Map",
    heroTitle: "宫古岛无人机地图",
    heroLead: "从地图寻找宫古岛绝景地点，并通过无人机影像预览现场风景。",
    openMap: "打开地图寻找绝景",
    mapFilter: "按区域查找",
    close: "关闭",
    tourCta: "查看相关行程",
    toursTitle: "推荐行程",
    popularTitle: "热门绝景地点",
    howTitle: "使用方式很简单",
    allTours: "查看所有行程",
    relatedTours: "相关行程",
    spotList: "所有地点",
    details: "查看详情",
    howSteps: [
      ["从地图寻找", "直接从地图选择想去的绝景地点。"],
      ["观看影像", "通过无人机影像确认现场氛围。"],
      ["查看行程", "继续查看包含该地点的推荐行程。"],
      ["留下回忆", "把喜欢的景色变成旅行目的地。"],
    ],
  },
  한국어: {
    navTitle: "미야코지마 드론 맵",
    heroKicker: "Miyako Drone Map",
    heroTitle: "미야코지마 드론 맵",
    heroLead: "지도에서 미야코지마의 절경 스팟을 고르고 드론 영상으로 먼저 만나보세요.",
    openMap: "맵에서 절경 찾기",
    mapFilter: "지역으로 찾기",
    close: "닫기",
    tourCta: "이 스팟의 투어 보기",
    toursTitle: "추천 투어",
    popularTitle: "인기 절경 스팟",
    howTitle: "사용 방법은 간단해요",
    allTours: "모든 투어 보기",
    relatedTours: "관련 투어",
    spotList: "모든 스팟",
    details: "자세히 보기",
    howSteps: [
      ["지도에서 찾기", "지도에서 마음에 드는 절경 스팟을 고를 수 있어요."],
      ["영상 보기", "드론 영상으로 현장의 분위기를 미리 확인해요."],
      ["투어 보기", "그 장소와 어울리는 투어로 바로 이어집니다."],
      ["특별한 추억으로", "마음에 든 풍경을 여행 목적지로 만들어보세요."],
    ],
  },
};

const videoBase = "https://res.cloudinary.com/drxijtp8g/video/upload/";
const DRONE_MAP_HERO_YOUTUBE_ID = "PUESenfmZW4";
const MAP_CENTER = { lat: 24.84, lng: 125.245 };
const MIN_MAP_ZOOM = 10;
const MAX_MAP_ZOOM = 13;
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyBGMjAtOf13SZ5FDNjmt7XVlcSRcurUbBE";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const googleMapOptions = {
  mapTypeId: "hybrid",
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
  maxZoom: MAX_MAP_ZOOM,
  minZoom: MIN_MAP_ZOOM,
};

const spots = [
  {
    id: "maehama",
    name: "与那覇前浜ビーチ",
    kana: "よなはまえはまビーチ / 宮古島市",
    area: "ビーチ",
    lead:
      "宮古島を代表する絶景ビーチ。白い砂浜と透明度抜群の海が広がり、ドローンで見ると海のグラデーションが美しく映ります。",
    image: img("v1784350164/DJI_20260708172917_0004_D_n0pzoh.webp", {
      width: 900,
      height: 560,
    }),
    video: `${videoBase}v1785170045/Timeline_1_otl8ph.mp4`,
    tags: ["ビーチ", "絶景スポット", "フォトジェニック", "おすすめ"],
    info: [
      ["ベストシーズン", "4月〜10月"],
      ["アクセス", "宮古空港から車で約15分"],
      ["おすすめ時間帯", "午前〜夕方"],
    ],
    location: { lat: 24.7359, lng: 125.2632 },
    tourIds: ["oneDayAesthetic", "surfing"],
  },
  {
    id: "ikema",
    name: "池間大橋",
    kana: "いけまおおはし / 池間島",
    area: "橋・ドライブ",
    lead:
      "池間ブルーを一望できるドライブスポット。橋と海を空から撮ると、宮古島らしいスケール感が出ます。",
    image: img("v1785003708/DJI_20260707132223_0047_D_small_no9d5s.jpg", {
      width: 900,
      height: 560,
    }),
    video: `${videoBase}v1785169947/Timeline_1_jmzpco.mp4`,
    tags: ["橋", "ドライブ", "絶景", "ドローン"],
    info: [
      ["見どころ", "橋と宮古ブルー"],
      ["アクセス", "市街地から車で約30分"],
      ["おすすめ時間帯", "晴れた日中"],
    ],
    location: { lat: 24.914, lng: 125.253 },
    tourIds: ["oneDayAesthetic"],
  },
  {
    id: "yabiji",
    name: "八重干瀬",
    kana: "やびじ / 池間島沖",
    area: "海",
    lead:
      "日本最大級のサンゴ礁群。海況が合う日の透明度は圧倒的で、空から見ると宮古島の海の広さを感じられます。",
    image: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp", {
      width: 900,
      height: 560,
    }),
    video: `${videoBase}v1785170133/Timeline_1_fq4wdw.mp4`,
    tags: ["海", "サンゴ礁", "宮古ブルー", "絶景"],
    info: [
      ["見どころ", "サンゴ礁と透明度"],
      ["アクセス", "ツアー参加がおすすめ"],
      ["おすすめ時間帯", "海況が良い午前"],
    ],
    location: { lat: 25.035, lng: 125.17 },
    tourIds: ["hiddenSup", "oneDayAesthetic"],
  },
  {
    id: "higashi",
    name: "東平安名崎",
    kana: "ひがしへんなざき / 宮古島市",
    area: "絶景",
    lead:
      "宮古島の東端に伸びる岬。海に囲まれた地形と灯台が美しく、ダイナミックな空撮に向いています。",
    image: img("v1785006839/DJI_20260723120636_0026_D_small_bgbdwa.jpg", {
      width: 900,
      height: 560,
    }),
    video: `${videoBase}v1785170245/Timeline_1_pzilcn.mp4`,
    tags: ["岬", "灯台", "絶景", "ドローン"],
    info: [
      ["見どころ", "岬と灯台"],
      ["アクセス", "市街地から車で約35分"],
      ["おすすめ時間帯", "朝〜夕方"],
    ],
    location: { lat: 24.7195, lng: 125.4684 },
    tourIds: ["oneDayAesthetic"],
  },
  {
    id: "kurima",
    name: "来間大橋",
    kana: "くりまおおはし / 来間島",
    area: "橋・ドライブ",
    lead:
      "来間島へ続く橋と透明な海が美しいスポット。カフェ巡りやサンセット前のドライブにも合わせやすい場所です。",
    image: img("v1783928437/DJI_20260707115907_0008_D_xag4ot.webp", {
      width: 900,
      height: 560,
    }),
    video: `${videoBase}v1785309244/sup2_lgldqv.mp4`,
    tags: ["橋", "ドライブ", "カフェ", "海"],
    info: [
      ["見どころ", "橋と海の色"],
      ["アクセス", "前浜から車ですぐ"],
      ["おすすめ時間帯", "午前〜サンセット前"],
    ],
    location: { lat: 24.7207, lng: 125.2555 },
    tourIds: ["oneDayAesthetic"],
  },
  {
    id: "secret-sup",
    name: "秘境SUPスポット",
    kana: "SUPでしか行けない海",
    area: "秘境",
    lead:
      "SUPでしか近づけない静かな海。誰もいないビーチと透明な水面を、写真・動画・ドローンで残せます。",
    image: img("v1785776522/DJI_20260802131800_0033_D_drlb1i.webp", {
      width: 900,
      height: 560,
    }),
    video: hiddenSupTour.detail.videoGallery[0].src,
    tags: ["秘境SUP", "シークレットビーチ", "貸切", "非日常"],
    info: [
      ["見どころ", "誰もいない海"],
      ["アクセス", "ガイド同行がおすすめ"],
      ["おすすめ時間帯", "海況に合わせて決定"],
    ],
    location: { lat: 24.825, lng: 125.135 },
    tourIds: ["hiddenSup"],
  },
];

const tourMap = {
  hiddenSup: hiddenSupTour,
  oneDayAesthetic: oneDayAestheticTour,
  surfing: surfingTour,
};

const formatPrice = (tour) => {
  const price = tour.salePrice || tour.originalPrice;
  if (!price?.amount) return "";

  const unit = price.unit === "group" ? "グループ" : "人";
  return `¥${price.amount.toLocaleString()} / ${unit}`;
};

const DroneMapPage = () => {
  const { lang } = useLanguage();
  const t = labels[lang] || labels.JP;
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [selectedId, setSelectedId] = useState(spots[0].id);
  const [mapZoom, setMapZoom] = useState(11);

  const selectedSpot = useMemo(
    () => spots.find((spot) => spot.id === selectedId) || spots[0],
    [selectedId],
  );

  const relatedTours = selectedSpot.tourIds
    .map((id) => tourMap[id])
    .filter(Boolean);

  const featuredTours = [oneDayAestheticTour, hiddenSupTour, surfingTour];

  return (
    <>
      <Header />
      <main className={s.root}>
        <section className={s.hero}>
          <iframe
            className={s.heroVideo}
            src={`https://www.youtube.com/embed/${DRONE_MAP_HERO_YOUTUBE_ID}?autoplay=1&mute=1&loop=1&playlist=${DRONE_MAP_HERO_YOUTUBE_ID}&controls=0&playsinline=1&modestbranding=1&rel=0&vq=hd1080&hd=1`}
            title="宮古島ドローンマップ"
            allow="autoplay; encrypted-media; picture-in-picture"
            aria-hidden="true"
            tabIndex="-1"
          />
          <div className={s.heroShade} />
          <div className={s.heroContent}>
            <h1>{t.heroTitle}</h1>
            <span>{t.heroLead}</span>
          </div>
        </section>

        <section className={s.mapSection} id="drone-map">
          <div className={s.mapShell}>
            <div className={s.mapCanvas}>
              {isMapLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={MAP_CENTER}
                  zoom={mapZoom}
                  options={googleMapOptions}
                  onZoomChanged={() => {}}
                  onLoad={(map) => {
                    map.addListener("zoom_changed", () => {
                      setMapZoom(map.getZoom() || 11);
                    });
                  }}
                >
                  {spots.map((spot) => (
                    <MarkerF
                      key={spot.id}
                      position={spot.location}
                      title={spot.name}
                      label={{
                        text: spot.name,
                        color:
                          selectedSpot.id === spot.id ? "#f43c7f" : "#0d2f50",
                        fontSize: "11px",
                        fontWeight: "900",
                        className: s.markerLabel,
                      }}
                      onClick={() => setSelectedId(spot.id)}
                      zIndex={selectedSpot.id === spot.id ? 20 : 10}
                    />
                  ))}
                </GoogleMap>
              ) : (
                <div className={s.mapFallback}>
                  {mapLoadError
                    ? "Google Mapを読み込めませんでした。時間をおいて再読み込みしてください。"
                    : "Google Mapを読み込んでいます。"}
                </div>
              )}
              <div className={s.mapWash} aria-hidden="true" />
            </div>

            <div className={s.mobileSpotList}>
              <p>{t.spotList}</p>
              <div>
                {spots.map((spot) => (
                  <button
                    type="button"
                    key={spot.id}
                    className={selectedSpot.id === spot.id ? s.selectedSpot : ""}
                    onClick={() => setSelectedId(spot.id)}
                  >
                    {spot.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <aside className={s.detailCard}>
            <div className={s.detailTop}>
              <span>● {selectedSpot.area}</span>
              <button type="button" onClick={() => setSelectedId(spots[0].id)}>
                ×
              </button>
            </div>

            <h2>{selectedSpot.name}</h2>
            <p className={s.kana}>{selectedSpot.kana}</p>

            <div className={s.videoFrame}>
              <video
                key={selectedSpot.video}
                src={selectedSpot.video}
                poster={selectedSpot.image}
                controls
                playsInline
                preload="metadata"
              />
            </div>

            <p className={s.spotLead}>{selectedSpot.lead}</p>

            <div className={s.tags}>
              {selectedSpot.tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>

            <dl className={s.infoGrid}>
              {selectedSpot.info.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>

            <Link className={s.primaryCta} to={relatedTours[0]?.href || "/tours"}>
              {t.tourCta}
              <span>→</span>
            </Link>
          </aside>
        </section>

        <section className={s.tourSection}>
          <div className={s.sectionHeader}>
            <div>
              <p>Tour</p>
              <h2>{t.toursTitle}</h2>
            </div>
            <Link to="/tours">{t.allTours}</Link>
          </div>

          <div className={s.tourGrid}>
            {featuredTours.map((tour) => (
              <article key={tour.id} className={s.tourCard}>
                <img src={tour.images?.[0] || tour.detail?.heroImage} alt="" loading="lazy" />
                <div>
                  <h3>
                    {tour.texts?.[lang]?.title ||
                      tour.detailTexts?.[lang]?.heroTitle ||
                      tour.texts?.JP?.title ||
                      tour.detailTexts?.JP?.heroTitle}
                  </h3>
                  <p>
                    <span>◷ 約{tour.time}</span>
                    <span>
                      ♙{" "}
                      {tour.texts?.[lang]?.people ||
                        tour.detailTexts?.[lang]?.people ||
                        tour.texts?.JP?.people ||
                        tour.detailTexts?.JP?.people}
                    </span>
                  </p>
                  <small>
                    {tour.texts?.[lang]?.notes ||
                      tour.detailTexts?.[lang]?.heroDescription ||
                      tour.texts?.JP?.notes ||
                      tour.detailTexts?.JP?.heroDescription}
                  </small>
                  <strong>{formatPrice(tour)}〜</strong>
                  <Link to={tour.href}>{t.details}</Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className={s.popularSection}>
          <div className={s.sectionHeader}>
            <div>
              <p>Spot</p>
              <h2>{t.popularTitle}</h2>
            </div>
          </div>

          <div className={s.popularRail}>
            {spots.map((spot) => (
              <button
                type="button"
                key={spot.id}
                className={selectedSpot.id === spot.id ? s.activeSpotCard : ""}
                onClick={() => setSelectedId(spot.id)}
              >
                <img src={spot.image} alt="" loading="lazy" />
                <strong>{spot.name}</strong>
              </button>
            ))}
          </div>
        </section>

        <section className={s.howSection}>
          <h2>{t.howTitle}</h2>
          <div className={s.howGrid}>
            {t.howSteps.map(([title, text], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DroneMapPage;
